/**
 * Read-only modal showing transfer context QR items and SR preferences.
 * Used in the Transfers Portal / Admin view.
 */

import {
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import type { QuestionnaireResponse, ServiceRequest } from '@medplum/fhirtypes';
import {
  extractTransferQrItems,
  extractServiceRequestPreferences,
} from '../../../lib/transferService';
import {
  TRANSFER_REASON_DISPLAY,
  isTransferReasonCode,
} from '../../../lib/transferFhirConstants';

export interface TransferDetailModalProps {
  opened: boolean;
  onClose: () => void;
  clientName?: string;
  episodeDisplay?: string;
  status?: string;
  fromProvider?: string;
  paymentType?: string;
  submissionDate?: string;
  questionnaireResponse?: QuestionnaireResponse;
  serviceRequest?: ServiceRequest;
}

function reasonLabel(code: string): string {
  return isTransferReasonCode(code) ? TRANSFER_REASON_DISPLAY[code] : code;
}

export function TransferDetailModal({
  opened,
  onClose,
  clientName,
  episodeDisplay,
  status,
  fromProvider,
  paymentType,
  submissionDate,
  questionnaireResponse,
  serviceRequest,
}: TransferDetailModalProps): JSX.Element {
  const qrItems = questionnaireResponse
    ? extractTransferQrItems(questionnaireResponse)
    : undefined;

  const prefs = serviceRequest
    ? extractServiceRequestPreferences(serviceRequest)
    : undefined;

  return (
    <Modal opened={opened} onClose={onClose} title="Transfer details" size="lg">
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Client
            </Text>
            <Text size="sm" fw={500}>
              {clientName ?? '—'}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Episode of Care
            </Text>
            <Text size="sm" fw={500}>
              {episodeDisplay ?? '—'}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Status
            </Text>
            <Badge
              variant="light"
              color={status === 'transfer' ? 'yellow' : 'blue'}
              size="sm"
            >
              {status === 'transfer' ? 'Pending Transfer' : status === 'consultation' ? 'Pending Consultation' : status ?? '—'}
            </Badge>
          </div>
        </SimpleGrid>

        <Divider />

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              From Provider
            </Text>
            <Text size="sm">{fromProvider ?? '—'}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Payment Type
            </Text>
            <Text size="sm">{paymentType ?? '—'}</Text>
          </div>
          {submissionDate && (
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Submission Date
              </Text>
              <Text size="sm">{submissionDate}</Text>
            </div>
          )}
        </SimpleGrid>

        {qrItems && (
          <>
            <Divider label="Transfer reasons" labelPosition="left" />
            <Group gap="xs" wrap="wrap">
              {qrItems.transferReasons.map((code) => (
                <Badge key={code} variant="light" color="gray">
                  {reasonLabel(code)}
                </Badge>
              ))}
            </Group>

            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                Transfer Explanation
              </Text>
              <Textarea
                value={qrItems.transferExplanation ?? ''}
                readOnly
                minRows={3}
                styles={{
                  input: {
                    backgroundColor: 'var(--mantine-color-gray-0)',
                  },
                }}
              />
            </div>

            {qrItems.recommendedClinicians && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                  Recommended Clinicians
                </Text>
                <Text size="sm">{qrItems.recommendedClinicians}</Text>
              </div>
            )}
          </>
        )}

        {prefs && (
          <>
            <Divider label="Matching preferences" labelPosition="left" />
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {prefs.sessionFormat && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Session Format
                  </Text>
                  <Text size="sm">{prefs.sessionFormat}</Text>
                </div>
              )}
              {prefs.areasOfFocus && prefs.areasOfFocus.length > 0 && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Areas of Focus
                  </Text>
                  <Text size="sm">{prefs.areasOfFocus.join(', ')}</Text>
                </div>
              )}
              {prefs.providerPreferences &&
                prefs.providerPreferences.length > 0 && (
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Provider Preferences
                    </Text>
                    <Text size="sm">
                      {prefs.providerPreferences.join(', ')}
                    </Text>
                  </div>
                )}
              {prefs.availability && prefs.availability.length > 0 && (
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Availability
                  </Text>
                  <Text size="sm">{prefs.availability.join(', ')}</Text>
                </div>
              )}
            </SimpleGrid>
          </>
        )}

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
